import React, { useEffect, useState } from "react";
import { hrApi } from "../../services/hrApi";

export default function HRSettingsPage() {
  const [loading, setLoading] = useState(true);

  const [departments, setDepartments] = useState([]);
  const [teams, setTeams] = useState([]);
  const [positions, setPositions] = useState([]);
  const [people, setPeople] = useState([]);

  const [departmentSaving, setDepartmentSaving] = useState(false);
  const [teamSaving, setTeamSaving] = useState(false);
  const [positionSaving, setPositionSaving] = useState(false);

  const [departmentForm, setDepartmentForm] = useState({
    name: "",
    code: "",
    description: "",
    manager_person_id: "",
    parent_department_id: "",
    active_flag: 1,
  });

  const [teamForm, setTeamForm] = useState({
    department_id: "",
    name: "",
    code: "",
    description: "",
    lead_person_id: "",
    active_flag: 1,
  });

  const [positionForm, setPositionForm] = useState({
    title: "",
    code: "",
    description: "",
    seniority_level: "",
    department_id: "",
    active_flag: 1,
  });

  async function load() {
    try {
      setLoading(true);

      const [depRes, teamRes, posRes, peopleRes] = await Promise.all([
        hrApi.getDepartments(),
        hrApi.getTeams(),
        hrApi.getPositions(),
        hrApi.getPeople({ status: "active" }),
      ]);

      setDepartments(depRes.data?.data || []);
      setTeams(teamRes.data?.data || []);
      setPositions(posRes.data?.data || []);
      setPeople(peopleRes.data?.data || []);
    } catch (err) {
      console.error("Failed to load HR settings:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function handleCreateDepartment(e) {
    e.preventDefault();

    try {
      setDepartmentSaving(true);

      await hrApi.createDepartment({
        name: departmentForm.name,
        code: departmentForm.code,
        description: departmentForm.description || null,
        manager_person_id: departmentForm.manager_person_id ? Number(departmentForm.manager_person_id) : null,
        parent_department_id: departmentForm.parent_department_id ? Number(departmentForm.parent_department_id) : null,
        active_flag: Number(departmentForm.active_flag || 1),
      });

      setDepartmentForm({
        name: "",
        code: "",
        description: "",
        manager_person_id: "",
        parent_department_id: "",
        active_flag: 1,
      });

      await load();
    } catch (err) {
      console.error("Failed to create department:", err);
      alert(err?.response?.data?.message || "Failed to create department.");
    } finally {
      setDepartmentSaving(false);
    }
  }

  async function handleCreateTeam(e) {
    e.preventDefault();

    try {
      setTeamSaving(true);

      if (!teamForm.department_id) {
        alert("Department is required.");
        return;
      }

      await hrApi.createTeam({
        department_id: Number(teamForm.department_id),
        name: teamForm.name,
        code: teamForm.code,
        description: teamForm.description || null,
        lead_person_id: teamForm.lead_person_id ? Number(teamForm.lead_person_id) : null,
        active_flag: Number(teamForm.active_flag || 1),
      });

      setTeamForm({
        department_id: "",
        name: "",
        code: "",
        description: "",
        lead_person_id: "",
        active_flag: 1,
      });

      await load();
    } catch (err) {
      console.error("Failed to create team:", err);
      alert(err?.response?.data?.message || "Failed to create team.");
    } finally {
      setTeamSaving(false);
    }
  }

  async function handleCreatePosition(e) {
    e.preventDefault();

    try {
      setPositionSaving(true);

      await hrApi.createPosition({
        title: positionForm.title,
        code: positionForm.code,
        description: positionForm.description || null,
        seniority_level: positionForm.seniority_level || null,
        department_id: positionForm.department_id ? Number(positionForm.department_id) : null,
        active_flag: Number(positionForm.active_flag || 1),
      });

      setPositionForm({
        title: "",
        code: "",
        description: "",
        seniority_level: "",
        department_id: "",
        active_flag: 1,
      });

      await load();
    } catch (err) {
      console.error("Failed to create position:", err);
      alert(err?.response?.data?.message || "Failed to create position.");
    } finally {
      setPositionSaving(false);
    }
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">HR Settings</h1>
        <p className="text-sm text-gray-500">
          Departments, teams, positions i osnovna organizaciona struktura.
        </p>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">Učitavanje...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Department form */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Create department</h2>

              <form onSubmit={handleCreateDepartment} className="space-y-3">
                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Department name"
                  value={departmentForm.name}
                  onChange={(e) => setDepartmentForm((s) => ({ ...s, name: e.target.value }))}
                  required
                />

                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Code"
                  value={departmentForm.code}
                  onChange={(e) => setDepartmentForm((s) => ({ ...s, code: e.target.value }))}
                  required
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Description"
                  value={departmentForm.description}
                  onChange={(e) => setDepartmentForm((s) => ({ ...s, description: e.target.value }))}
                />

                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={departmentForm.manager_person_id}
                  onChange={(e) => setDepartmentForm((s) => ({ ...s, manager_person_id: e.target.value }))}
                >
                  <option value="">Manager</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name || `${p.first_name} ${p.last_name}`}
                    </option>
                  ))}
                </select>

                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={departmentForm.parent_department_id}
                  onChange={(e) => setDepartmentForm((s) => ({ ...s, parent_department_id: e.target.value }))}
                >
                  <option value="">Parent department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>

                <button
                  type="submit"
                  disabled={departmentSaving}
                  className="px-4 py-2 rounded-xl bg-black text-white"
                >
                  {departmentSaving ? "Saving..." : "Save department"}
                </button>
              </form>
            </div>

            {/* Team form */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Create team</h2>

              <form onSubmit={handleCreateTeam} className="space-y-3">
                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={teamForm.department_id}
                  onChange={(e) => setTeamForm((s) => ({ ...s, department_id: e.target.value }))}
                  required
                >
                  <option value="">Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>

                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Team name"
                  value={teamForm.name}
                  onChange={(e) => setTeamForm((s) => ({ ...s, name: e.target.value }))}
                  required
                />

                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Code"
                  value={teamForm.code}
                  onChange={(e) => setTeamForm((s) => ({ ...s, code: e.target.value }))}
                  required
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Description"
                  value={teamForm.description}
                  onChange={(e) => setTeamForm((s) => ({ ...s, description: e.target.value }))}
                />

                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={teamForm.lead_person_id}
                  onChange={(e) => setTeamForm((s) => ({ ...s, lead_person_id: e.target.value }))}
                >
                  <option value="">Team lead</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.full_name || `${p.first_name} ${p.last_name}`}
                    </option>
                  ))}
                </select>

                <button
                  type="submit"
                  disabled={teamSaving}
                  className="px-4 py-2 rounded-xl bg-black text-white"
                >
                  {teamSaving ? "Saving..." : "Save team"}
                </button>
              </form>
            </div>

            {/* Position form */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Create position</h2>

              <form onSubmit={handleCreatePosition} className="space-y-3">
                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Position title"
                  value={positionForm.title}
                  onChange={(e) => setPositionForm((s) => ({ ...s, title: e.target.value }))}
                  required
                />

                <input
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Code"
                  value={positionForm.code}
                  onChange={(e) => setPositionForm((s) => ({ ...s, code: e.target.value }))}
                  required
                />

                <textarea
                  className="w-full border rounded-xl px-3 py-2"
                  placeholder="Description"
                  value={positionForm.description}
                  onChange={(e) => setPositionForm((s) => ({ ...s, description: e.target.value }))}
                />

                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={positionForm.seniority_level}
                  onChange={(e) => setPositionForm((s) => ({ ...s, seniority_level: e.target.value }))}
                >
                  <option value="">Seniority level</option>
                  <option value="junior">Junior</option>
                  <option value="mid">Mid</option>
                  <option value="senior">Senior</option>
                  <option value="lead">Lead</option>
                  <option value="manager">Manager</option>
                  <option value="director">Director</option>
                  <option value="executive">Executive</option>
                </select>

                <select
                  className="w-full border rounded-xl px-3 py-2"
                  value={positionForm.department_id}
                  onChange={(e) => setPositionForm((s) => ({ ...s, department_id: e.target.value }))}
                >
                  <option value="">Department</option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>

                <button
                  type="submit"
                  disabled={positionSaving}
                  className="px-4 py-2 rounded-xl bg-black text-white"
                >
                  {positionSaving ? "Saving..." : "Save position"}
                </button>
              </form>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            {/* Departments list */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Departments</h2>
              <div className="space-y-3">
                {departments.length === 0 ? (
                  <div className="text-sm text-gray-500">Nema department-a.</div>
                ) : (
                  departments.map((row) => (
                    <div key={row.id} className="border rounded-xl p-3">
                      <div className="font-medium">{row.name}</div>
                      <div className="text-sm text-gray-500">{row.code}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Manager: {row.manager_name || "-"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Teams list */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Teams</h2>
              <div className="space-y-3">
                {teams.length === 0 ? (
                  <div className="text-sm text-gray-500">Nema team-ova.</div>
                ) : (
                  teams.map((row) => (
                    <div key={row.id} className="border rounded-xl p-3">
                      <div className="font-medium">{row.name}</div>
                      <div className="text-sm text-gray-500">{row.code}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Department: {row.department_name || "-"}
                      </div>
                      <div className="text-xs text-gray-400">
                        Lead: {row.lead_name || "-"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Positions list */}
            <div className="bg-white rounded-2xl border shadow-sm p-4">
              <h2 className="text-lg font-semibold mb-4">Positions</h2>
              <div className="space-y-3">
                {positions.length === 0 ? (
                  <div className="text-sm text-gray-500">Nema pozicija.</div>
                ) : (
                  positions.map((row) => (
                    <div key={row.id} className="border rounded-xl p-3">
                      <div className="font-medium">{row.title}</div>
                      <div className="text-sm text-gray-500">{row.code}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Department: {row.department_name || "-"}
                      </div>
                      <div className="text-xs text-gray-400">
                        Seniority: {row.seniority_level || "-"}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}